import { Injectable } from '@angular/core';
import { ExistingPolicyInForce } from '../../../../common/js/customer/ExistingPolicyInForce';
import { CommonUtils } from '../../../../common/js/static/CommonUtils';
import { CoverpathPolicy } from '../../../classes/coverpath_subclasses/CoverpathPolicy';
import { AmorphicService } from '../amorphic/amorphic.service';

@Injectable()
export class AutofillService {
  private readonly cities = {
    AL: { city: 'Montgomery', zip: '36101' },
    AK: { city: 'Juneau', zip: '99801' },
    AZ: { city: 'Phoenix', zip: '85001' },
    AR: { city: 'Little Rock', zip: '72201' },
    CA: { city: 'Sacramento', zip: '94203' },
    CO: { city: 'Denver', zip: '80201' },
    CT: { city: 'Hartford', zip: '06101' },
    DC: { city: 'Washington', zip: '20001' },
    DE: { city: 'Dover', zip: '19901' },
    FL: { city: 'Tallahassee', zip: '32301' },
    GA: { city: 'Atlanta', zip: '30301' },
    HI: { city: 'Honolulu', zip: '96801' },
    ID: { city: 'Boise', zip: '83701' },
    IL: { city: 'Springfield', zip: '62701' },
    IN: { city: 'Indianapolis', zip: '46201' },
    IA: { city: 'Des Moines', zip: '50301' },
    KS: { city: 'Topeka', zip: '66601' },
    KY: { city: 'Frankfort', zip: '40601' },
    LA: { city: 'Baton Rouge', zip: '70801' },
    ME: { city: 'Augusta', zip: '04330' },
    MD: { city: 'Annapolis', zip: '21401' },
    MA: { city: 'Boston', zip: '02108' },
    MI: { city: 'Lansing', zip: '48901' },
    MN: { city: 'St. Paul', zip: '55101' },
    MS: { city: 'Jackson', zip: '39201' },
    MO: { city: 'Jefferson City', zip: '65101' },
    MT: { city: 'Helena', zip: '59601' },
    NE: { city: 'Lincoln', zip: '68501' },
    NV: { city: 'Carson City', zip: '89701' },
    NH: { city: 'Concord', zip: '03301' },
    NJ: { city: 'Trenton', zip: '08601' },
    NM: { city: 'Santa Fe', zip: '87501' },
    NY: { city: 'Albany', zip: '12201' },
    NC: { city: 'Raleigh', zip: '27601' },
    ND: { city: 'Bismarck', zip: '58501' },
    OH: { city: 'Columbus', zip: '43201' },
    OK: { city: 'Oklahoma', zip: '73101' },
    OR: { city: 'Salem', zip: '97301' },
    PA: { city: 'Harrisburg', zip: '17101' },
    RI: { city: 'Providence', zip: '02901' },
    SC: { city: 'Columbia', zip: '29201' },
    SD: { city: 'Pierre', zip: '57501' },
    TN: { city: 'Nashville', zip: '37201' },
    TX: { city: 'Austin', zip: '73301' },
    UT: { city: 'Salt Lake City', zip: '84101' },
    VT: { city: 'Montpelier', zip: '05601' },
    VA: { city: 'Richmond', zip: '23218' },
    WA: { city: 'Olympia', zip: '98501' },
    WV: { city: 'Charleston', zip: '25301' },
    WI: { city: 'Madison', zip: '53701' },
    WY: { city: 'Cheyenne', zip: '82001' },
  };

  constructor(private amorphicService: AmorphicService) {}

  public autoFillCoverpath(policy: CoverpathPolicy): void {
    if (!this.amorphicService.controller.nonProdMode) {
      return;
    }
    this.autofillInsured(policy);
    this.autofillOwner(policy);
    this.autofillMedical(policy);
    this.autofillPurpose(policy);
    this.autofillBeneficiaries(policy);
    this.autofillExistingPolicies(policy);
    this.autofillPolicyInformation(policy);

    if (policy.hasCirRider) {
      this.autofillChronicIllnessRiderSupplement(policy);
    }

    this.autofillAcknowledgements(policy);
    this.autofillPaymentMethod(policy);

    if (policy.application.sourceOfFunds) {
      policy.application.sourceOfFunds.payingWithRetirementAccount = false;
    }
  }

  private autofillInsured(policy: CoverpathPolicy): void {
    const insured = policy.insured;
    const insuredPerson = insured.person;
    const financials = policy.insured.financials;
    const riskFactors = policy.insured.riskFactors;

    policy.insuredType = 'you';
    policy.insuredTypeTrigger();

    insured.address.street = '38 Haggerty Hill Rd';
    const { city, zip } = this.cities[policy.insured.address.state];
    insured.address.city = city;
    insured.address.zip = zip;
    insured.phone.number = '1111111111';
    insured.heightFeet = 5;
    insured.heightFeetTrigger();
    insured.heightInches = 11;
    insured.heightInchesTrigger();
    insured.weight = 200;
    insuredPerson.email = policy.customer.email;
    insuredPerson.emailTrigger();
    insuredPerson.idNumber = 'E0980890890';
    insuredPerson.idNumberTrigger();
    insuredPerson.idState = 'NY';
    insuredPerson.idExpires = new Date('11/11/2028');
    insuredPerson.residencyStatus = 'citizen';
    insuredPerson.currentInsurer = 'Metlife';
    insuredPerson.currentInsurerGroup = 'whatever';
    insuredPerson.currentInsuranceID = '888';
    insuredPerson.firstName = policy.customer.firstName;
    insuredPerson.lastName = policy.customer.lastName;
    insuredPerson.middleName = 'V';
    insuredPerson.birthCountry = 'US';
    insuredPerson.birthState = 'NY';
    insuredPerson.SSN = this.generateRandomSSN();

    financials.employed = true;
    financials.employedTrigger();
    financials.employerName = 'NASA';
    financials.occupation = 'Accountant';
    financials.occupationTrigger();
    financials.annualIncome = 100000;
    financials.netWorth = 300000;
    financials.duties = 'Office work';
    financials.employerAddress.zip = '77007';
    financials.employerAddress.zipTrigger();
    financials.employerAddress.street = '2101 E NASA Pkwy';
    financials.employerAddress.city = 'Houston';
    financials.employerAddress.state = 'TX';
    financials.coverageExistingAmt = 1000000;
    financials.hasBankruptcyHistory = 'false';

    riskFactors.armedForces = false;
    riskFactors.medicalDischarge = false;
    riskFactors.isFelonios = false;
    riskFactors.isPilot = false;
    riskFactors.hasAvocations = false;
    riskFactors.mvDUI = false;
    riskFactors.mvAccident = false;
    riskFactors.mvDrivingWhileSuspended = false;
    riskFactors.mvDrivingWhileSuspendedTrigger();
    riskFactors.mvLicenseSuspended = false;
    riskFactors.disabled = false;
    riskFactors.anticipatingForeignTravel = 'No';
    riskFactors.isSmokester = false;
    riskFactors.isSmokesterTrigger();
    riskFactors.useTobaccoCessationMeds = false;
    riskFactors.useAlcohol = false;
    riskFactors.weightLoss = 'NoIhavenot';
    riskFactors.bingeDrinkingMonthly = 1;
    riskFactors.isPregnant = false;
    riskFactors.whenLastSmokedOrUsedMarijuana = 'never';
    riskFactors.howManyAlcohol = 1;
    riskFactors.howManyAlcoholTrigger();
  }

  private autofillOwner(policy: CoverpathPolicy): void {
    policy.ownerClassification = 'you';
    policy.ownerClassificationTrigger();
    policy.hasSecondaryAddressee = false;
  }

  private autofillMedical(policy: CoverpathPolicy): void {
    const familyHistory = policy.insured.familyHistory;
    const personalHistory = policy.insured.personalHistory;
    familyHistory.motherLiving = 'unknown';
    familyHistory.fatherLiving = 'unknown';
    familyHistory.motherLivingTrigger();
    familyHistory.fatherLivingTrigger();
    familyHistory.brothersLiving = 'Nobiologicalbrothers';
    familyHistory.sistersLiving = 'Nobiologicalsisters';
    familyHistory.hasConditions = 'No';
    familyHistory.hasConditionsTrigger();
    familyHistory.hadSeriousCondition = 'No';
    personalHistory.hadNoCondition = true;
    personalHistory.hadDrugUse = false;
    personalHistory.hadAlcoholAbuse = false;
    personalHistory.hadMastectomy = false;
    personalHistory.hadMastectomyTrigger();
    personalHistory.hadECG = false;
    personalHistory.hadInsuranceRestricted = false;
    personalHistory.hadDisabilityClaim = false;
    personalHistory.hadFullDiagnosisExclHIV = 'No';
    personalHistory.hadBloodTransfusion = false;
    personalHistory.hadBloodTransfusionTrigger();
    personalHistory.hadHeartSurgery = false;
    personalHistory.hadHeartSurgeryTrigger();
    personalHistory.hasProstatectomy = false;
    personalHistory.hasProstatectomyTrigger();
    personalHistory.hadNoECGDL = true;
    personalHistory.hadExamOther = false;
    personalHistory.hadTreatmentOther = false;
    personalHistory.hadTreatmentOtherTrigger();
    personalHistory.hadRXOther = false;
    personalHistory.hadRXOtherTrigger();
    personalHistory.primaryPhysician = false;
    personalHistory.primaryPhysicianTrigger();
    personalHistory.hivPositiveWhichAddress = 'insured_address';
    personalHistory.hivPositiveWhichAddressTrigger();
    personalHistory.hadImmunoDeficiency = false;
  }

  private autofillPurpose(policy: CoverpathPolicy): void {
    policy.trustOrOtherEntity = undefined;
    policy.premiumSourceYesNoOther = 'savings';
    policy.reasonForInsuranceShort = 'FP';
    policy.reasonForInsuranceShortTrigger();
    policy.reasonForInsurance = 'P';
    policy.personalReasonForInsurance = 'familyProtection';
  }

  private autofillBeneficiaries(policy: CoverpathPolicy): void {
    const bene = policy.primaryBeneficiary[0];
    bene.person.firstName = 'Karen';
    bene.person.middleName = 'M';
    bene.person.lastName = 'Burke';
    bene.address.street = '38 Haggerty Hill Rd';
    bene.address.zip = '10603';
    bene.address.zipTrigger();
    bene.address.city = 'White Plains';
    bene.address.state = 'NY';
    bene.phone.type = '1';
    bene.phone.typeTrigger();
    bene.phone.number = '1239889888';
    bene.person.SSN = '988-98-9888';
    bene.person.dob = new Date('11/28/67');
    bene.person.dobTrigger();
    bene.relationship = 'Spouse';
    bene.ratio = 100;
    bene.perStirpes = true;
  }

  private autofillExistingPolicies(policy: CoverpathPolicy): void {
    const alirRider = policy.alirRider;
    const existingPolicies = policy.insured.existingPolicies;
    const ownerExistingPolicies = policy.owner.existingPolicies;

    // Cash value replacements (1035 Exchange)
    if (alirRider && alirRider.exchange1035 && alirRider.exchange1035.cashValueTransfer) {
      existingPolicies.hasExistingInforcePolicies = true;
      existingPolicies.hasExistingPolicies = true;
      existingPolicies.inForcePoliciesFaceTotal = 100000;
      const existingPolicy = this.amorphicService.controller.applicationController.createIterateClass(
        'ExistingPolicyInForce',
        policy.customer
      ) as ExistingPolicyInForce;
      existingPolicy.face = 100000;
      existingPolicy.purpose = 'P';
      existingPolicy.type = 'whole';
      existingPolicy.companyName = 'AXA';
      existingPolicy.owner = 'owner';
      existingPolicy.isReplacement = true;
      existingPolicy.number = '88888888';
      existingPolicy.cashValueTransfer = alirRider.exchange1035.cashValueTransfer;
      existingPolicy.cashValueTransferTrigger();
      existingPolicy.replacementLoanAmount = 0;
      existingPolicy.coverageStartDate = new Date();
      existingPolicy.isGroupPolicy = false;
      existingPolicies.existingInForcePolicyList = [existingPolicy];
      policy.alirLumpSumUses1035 = true;
      policy.alir1035ExchangeAmount = alirRider.exchange1035.cashValueTransfer;
      policy.alir1035Purpose = alirRider.exchange1035.applyTowards;
    } else {
      // No replacement
      existingPolicies.hasExistingPolicies = false;
      existingPolicies.hasExistingPoliciesTrigger();
      existingPolicies.inForcePoliciesFaceTotal = 0;
      existingPolicies.isReplacementOther = false;
      existingPolicies.hasExistingInforcePolicies = false;
      existingPolicies.hasExistingInforcePoliciesTrigger();
    }
    existingPolicies.replaceInforceApplication = false;
    ownerExistingPolicies.replaceInforceApplication = false;
    existingPolicies.replaceInforceApplicationTrigger();
    existingPolicies.isApplyingForPolicy = 'No';
    existingPolicies.isApplyingForPolicyTrigger();
  }

  private autofillChronicIllnessRiderSupplement(policy: CoverpathPolicy): void {
    policy.insured.chronicIllnessRiderSupplement.requiresHygieneAid = false;
    policy.insured.chronicIllnessRiderSupplement.requiresMachineAid = false;
    policy.insured.chronicIllnessRiderSupplement.requiresChoreAid = false;
    policy.insured.chronicIllnessRiderSupplement.hasAlzheimers = false;
    policy.insured.chronicIllnessRiderSupplement.hasAls = false;
    policy.insured.chronicIllnessRiderSupplement.hasCerebralPalsy = false;
    policy.insured.chronicIllnessRiderSupplement.hasCysticFibrosis = false;
    policy.insured.chronicIllnessRiderSupplement.hasDementia = false;
    policy.insured.chronicIllnessRiderSupplement.hasHuntingtons = false;
    policy.insured.chronicIllnessRiderSupplement.hasLiverCirrhosis = false;
    policy.insured.chronicIllnessRiderSupplement.hasMuscularDystrophy = false;
    policy.insured.chronicIllnessRiderSupplement.hasMyastheniaGravis = false;
    policy.insured.chronicIllnessRiderSupplement.hasBrainSyndrome = false;
    policy.insured.chronicIllnessRiderSupplement.hasParalysis = false;
    policy.insured.chronicIllnessRiderSupplement.hasParkinsons = false;
    policy.insured.chronicIllnessRiderSupplement.hasSchizophrenia = false;
    policy.insured.chronicIllnessRiderSupplement.hadAmnesia = false;
    policy.insured.chronicIllnessRiderSupplement.hadBoneOrJointCondition = false;
    policy.insured.chronicIllnessRiderSupplement.hadAtaxiaMyelitisNeuron = false;
    policy.insured.chronicIllnessRiderSupplement.hadLimitedMotion = false;
    policy.insured.chronicIllnessRiderSupplement.hadMemoryLoss = false;
    policy.insured.chronicIllnessRiderSupplement.hadTremorsOrNumbness = false;
    policy.insured.chronicIllnessRiderSupplement.hadFallLeadingToInjury = false;
    policy.insured.chronicIllnessRiderSupplement.receivedDisabilityBenefits = false;
    policy.insured.chronicIllnessRiderSupplement.coveredByMedicaid = false;
    policy.insured.chronicIllnessRiderSupplement.cirRecentlyRejected = false;
    policy.insured.chronicIllnessRiderSupplement.cirInForceCoverageAmount = 0;
  }

  private autofillPolicyInformation(policy: CoverpathPolicy): void {
    policy.hasEcp = false;
    policy.hasEcpTrigger();
    policy.isOwnerAlsoPayer = 'true';
    if (policy.isWholeLifePolicy()) {
      policy.wholeAPL = false;
    }
    if (policy.application.quote && policy.application.quote.paymentFrequency) {
      policy.paymentFrequency = CommonUtils.makePaymentFrequencySafeForPolicy(
        policy.application.quote.paymentFrequency
      );
    }
  }

  private autofillAcknowledgements(policy: CoverpathPolicy): void {
    policy.owner.isTINCorrect = true;
    policy.owner.financials.isNotFATCAWitholding = true;
    policy.owner.financials.isFATCAResidencyStatus = true;
  }

  private autofillPaymentMethod(policy: CoverpathPolicy): void {
    policy.isDOL = false;
    policy.paymentInApplication = 'now';
    policy.paymentAccountType = '2';
    policy.paymentAccountOwnerType = 'individual';
    policy.paymentBankName = 'bank';
    policy.paymentABA = '321321321'; // Bank Routing Number
    policy.paymentAccount = '123123123'; // Bank Account Number
    policy.hasContingentBeneficiary = false;
    policy.hasContingentBeneficiaryTrigger();
    policy.premiumSource = false;
    policy.waiver = false;
    policy.collaterallyAssigned = false;
    policy.economicIncentive = false;
    policy.beneficialInterest = false;
  }

  private generateRandomSSN(): string {
    const now = new Date();
    return `${now.getMonth() % 10}${now.getDate() + 10}-${now.getHours() + 10}-${now.getMinutes() + 10}${
      now.getSeconds() + 10
    }`;
  }
}
